import { useState, useRef, useCallback, useEffect } from 'react';
import { io } from 'socket.io-client';

const TURN_STATES = {
  IDLE: 'idle',
  USER_SPEAKING: 'user_speaking',
  PROCESSING: 'processing',
  AI_SPEAKING: 'ai_speaking',
};

export function useVoiceSession() {
  const [turnState, setTurnState] = useState(TURN_STATES.IDLE);
  const [transcript, setTranscript] = useState([]); // {role, content}[]
  const [currentUserText, setCurrentUserText] = useState('');
  const [topicsCovered, setTopicsCovered] = useState([]);
  const [readyToWrap, setReadyToWrap] = useState(false);
  const [extractedCount, setExtractedCount] = useState(0);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [generatingProfile, setGeneratingProfile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const streamRef = useRef(null);

  // Play audio from queue
  const playNextAudio = useCallback(async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      return;
    }

    isPlayingRef.current = true;
    const audioData = audioQueueRef.current.shift();

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const ctx = audioContextRef.current;
      const arrayBuffer = audioData instanceof ArrayBuffer ? audioData : audioData.buffer;
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      source.onended = () => playNextAudio();
      source.start(0);
    } catch (err) {
      console.error('[Voice] Audio playback error:', err);
      playNextAudio();
    }
  }, []);

  // Set up shared socket event listeners
  const setupSocketListeners = useCallback((socket, emitEvent, emitPayload) => {
    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      setError(null);
      socket.emit(emitEvent, emitPayload);
    });

    socket.on('connect_error', (err) => {
      setError('Connection failed: ' + err.message);
      setIsConnected(false);
    });

    socket.on('session_started', ({ sessionId: sid }) => {
      setSessionId(sid);
      setTurnState(TURN_STATES.PROCESSING);
    });

    socket.on('session_resumed', ({ sessionId: sid, transcript: savedTranscript, topicsCovered: tc, extractedCount: ec }) => {
      setSessionId(sid);
      if (savedTranscript) setTranscript(savedTranscript);
      if (tc) setTopicsCovered(tc);
      if (ec !== undefined) setExtractedCount(ec);
      setTurnState(TURN_STATES.PROCESSING); // Waiting for welcome-back greeting
    });

    socket.on('session_paused', () => {
      setIsPaused(true);
    });

    socket.on('user_transcript', ({ text, isFinal }) => {
      setCurrentUserText(text);
      if (isFinal) {
        setTranscript((prev) => [...prev, { role: 'user', content: text }]);
        setCurrentUserText('');
        setTurnState(TURN_STATES.PROCESSING);
      }
    });

    socket.on('processing', () => {
      setTurnState(TURN_STATES.PROCESSING);
    });

    socket.on('ai_speaking', ({ text }) => {
      setTurnState(TURN_STATES.AI_SPEAKING);
      setTranscript((prev) => [...prev, { role: 'assistant', content: text }]);
    });

    socket.on('ai_audio', (audioData) => {
      audioQueueRef.current.push(audioData);
      if (!isPlayingRef.current) {
        playNextAudio();
      }
    });

    socket.on('ai_done', ({ topicsCovered: tc, readyToWrap: rw, extractedCount: ec }) => {
      if (tc) setTopicsCovered(tc);
      if (rw) setReadyToWrap(true);
      if (ec !== undefined) setExtractedCount(ec);
      setTurnState(TURN_STATES.IDLE);
    });

    socket.on('session_status', ({ status }) => {
      if (status === 'generating_profile') {
        setGeneratingProfile(true);
      }
    });

    socket.on('session_complete', ({ profile: p, sessionId: sid }) => {
      setProfile(p);
      setGeneratingProfile(false);
      setSessionId(sid);
      stopRecording();
    });

    socket.on('error', ({ message }) => {
      setError(message);
      setTurnState(TURN_STATES.IDLE);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });
  }, [playNextAudio, stopRecording]);

  // Connect and start a new voice session
  const startSession = useCallback(() => {
    const token = localStorage.getItem('novadate_token');
    if (!token) { setError('Not authenticated'); return; }

    const socket = io('/voice-onboarding', {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
    });

    setupSocketListeners(socket, 'start_session');
  }, [setupSocketListeners]);

  // Connect and resume a paused voice session
  const resumeSession = useCallback((resumeSessionId) => {
    const token = localStorage.getItem('novadate_token');
    if (!token) { setError('Not authenticated'); return; }

    setIsPaused(false);

    const socket = io('/voice-onboarding', {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
    });

    setupSocketListeners(socket, 'resume_session', { sessionId: resumeSessionId });
  }, [setupSocketListeners]);

  // Start recording from microphone
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0 && socketRef.current?.connected) {
          event.data.arrayBuffer().then((buffer) => {
            socketRef.current.emit('audio_chunk', buffer);
          });
        }
      };

      // Send audio chunks every 250ms
      mediaRecorder.start(250);
      setTurnState(TURN_STATES.USER_SPEAKING);
    } catch (err) {
      setError('Microphone access denied: ' + err.message);
    }
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Toggle recording (push-to-talk)
  const toggleRecording = useCallback(() => {
    if (turnState === TURN_STATES.USER_SPEAKING) {
      stopRecording();
      socketRef.current?.emit('end_turn');
      setTurnState(TURN_STATES.PROCESSING);
    } else if (turnState === TURN_STATES.IDLE) {
      startRecording();
    }
  }, [turnState, startRecording, stopRecording]);

  // End the session
  const endSession = useCallback(() => {
    stopRecording();
    socketRef.current?.emit('end_session');
  }, [stopRecording]);

  // Pause the session (continue later)
  const pauseSession = useCallback(() => {
    stopRecording();
    socketRef.current?.emit('pause_session');
  }, [stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopRecording]);

  return {
    turnState,
    transcript,
    currentUserText,
    topicsCovered,
    readyToWrap,
    extractedCount,
    error,
    sessionId,
    profile,
    isConnected,
    generatingProfile,
    isPaused,
    startSession,
    resumeSession,
    pauseSession,
    startRecording,
    stopRecording,
    toggleRecording,
    endSession,
    TURN_STATES,
  };
}
